// How current the page is, in one line short enough to ignore.
//
// The dashboard reads a Fivetran mirror of Salesforce rather than Salesforce itself, and that
// mirror syncs on a schedule we do not control -- so the age of the *data* is a real fact that
// "refreshed just now" does not cover, and it has to stay on the page somewhere. What GRO-80
// changed is how much room it gets. The sync time, the next sync and the cadence used to be
// printed in full, in the busiest part of a header that non-technical people read twenty times a
// day. Now the healthy state says only how old the data is, and everything else -- including
// which build is answering -- is one click away.

import { Popover } from './Popover';
import type { Build, Freshness } from '../api/types';

export function SyncPill({
  freshness,
  build,
}: {
  freshness: Freshness | null;
  build: Build | undefined;
}) {
  // Nothing useful to say before the first payload lands. An empty pill would reserve space for
  // a fact we do not have yet, which is worse than the pill appearing with the data.
  if (!freshness) return null;

  const hoursBehind = freshness.minutes_behind / 60;
  const interval = freshness.typical_interval_minutes;

  // Amber only when a sync looks genuinely missed. Being a few hours behind is the normal state
  // of a twice-daily schedule and must not read as an alarm -- the same test the old line used.
  const isBehind = interval
    ? freshness.minutes_behind > interval * 1.5
    : hoursBehind > 24;

  const age = freshness.minutes_behind < 60
    ? `${Math.round(freshness.minutes_behind)}m ago`
    : `${hoursBehind.toFixed(1)}h ago`;

  // The detail, formatted once so the panel below is only markup.
  const syncedAt = String(freshness.synced_at).slice(0, 16).replace('T', ' ');
  const nextSync = freshness.next_sync_estimate
    ? new Date(freshness.next_sync_estimate)
      .toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : null;
  const cadence = interval ? `every ~${Math.round(interval / 60)}h` : null;
  const deployedAt = build?.started_at ? new Date(build.started_at).toLocaleString() : null;

  const summary = isBehind ? `Sync overdue · ${Math.round(hoursBehind)}h` : `Synced ${age}`;

  return (
    <Popover
      label={`Salesforce data is ${age}. Show sync detail`}
      triggerClass={'syncpill' + (isBehind ? ' late' : '')}
      trigger={<><span className="dot" aria-hidden="true" />{summary}</>}
      panelClass="syncpop"
    >
      <p className="plab">Salesforce mirror</p>
      <div className="poprow"><span>Data as of</span><b>{syncedAt}</b></div>
      {nextSync ? <div className="poprow"><span>Next sync</span><b>~{nextSync}</b></div> : null}
      {cadence ? <div className="poprow"><span>Cadence</span><b>{cadence}</b></div> : null}

      {/* Only when the schedule looks broken. The pill has already gone amber; this says what
          that means for the numbers on the page. */}
      {isBehind ? (
        <p className="popnote">
          A sync looks overdue, so these numbers may be older than the cadence suggests.
        </p>
      ) : null}

      {/* Which build is answering. This was printed on every page load before GRO-80, which is
          how it came to be read as decoration -- it belongs where somebody goes looking for it.

          Locally it says which *checkout* is answering, and calls itself the API rather than
          "this page", because off Render those can be two different working copies -- and the
          question that cost an afternoon on 28 Aug was which backend, not which commit. The
          rows are populated at all locally only since GRO-82; `build.commit` was empty there,
          so this whole block was hidden in the one place two backends can be running. */}
      {build?.commit ? (
        <>
          <hr />
          <p className="plab">{build.checkout ? 'Answered by' : 'This page'}</p>
          <div className="poprow"><span>Build</span><b>{build.commit}</b></div>
          {build.checkout
            ? <div className="poprow"><span>Checkout</span><b>{build.checkout}</b></div> : null}
          {build.branch
            ? <div className="poprow"><span>Branch</span><b>{build.branch}</b></div> : null}
          {deployedAt ? (
            <div className="poprow">
              <span>{build.checkout ? 'Started' : 'Deployed'}</span><b>{deployedAt}</b>
            </div>
          ) : null}
        </>
      ) : null}
    </Popover>
  );
}
